import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import { ClipLoader } from "react-spinners";
import { createPortal } from "react-dom";
import { Card, CardBody, Row, Col, Button } from "reactstrap";
import api from "../../../../../services/api"; // Importe o serviço de API
import "../../../../../assets/css/bigcalendar.css"; // Importe o CSS do react-big-calendar
import "./styles.css";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import FaturaDetalheModal, { rotuloSituacao } from "../FaturaDetalheModal";

moment.locale("pt-br");

const localizer = momentLocalizer(moment);
const getTimestamp = () =>
  typeof performance !== "undefined" && performance.now
    ? performance.now()
    : Date.now();

const DATE_CANDIDATES = [
  "start",
  "inicio",
  "dataInicial",
  "data_inicio",
  "dtInicio",
  "data",
  "date",
  "vencimento",
  "dataVencimento",
  "dtVencimento",
  "dueDate",
];

const END_DATE_CANDIDATES = [
  "end",
  "fim",
  "dataFinal",
  "data_final",
  "dtFim",
  "endDate",
  "dataTermino",
  "dtTermino",
];

const KNOWN_DATE_FORMATS = [
  moment.ISO_8601,
  "YYYY-MM-DDTHH:mm:ss.SSSZ",
  "YYYY-MM-DDTHH:mm:ssZ",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD",
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY",
];

// Título do evento a partir dos campos estruturados da API. Antes o valor era
// "adivinhado" quebrando o title por " - "; com transportadora "Tegma ... - SP"
// o pedaço "SP" virava valor e todo evento mostrava R$ 0,00.
const buildEventTitle = (event) => {
  if (!event || !event.numFatura) return null;
  const parts = [event.transportadora, event.numFatura];
  if (event.valor !== null && event.valor !== undefined) {
    parts.push(formatCurrency(Number(event.valor) || 0));
  }
  return parts.filter(Boolean).join(" - ");
};

const parseToDate = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "number") {
    const numericDate = new Date(value);
    return Number.isNaN(numericDate.getTime()) ? null : numericDate;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = moment(trimmed, KNOWN_DATE_FORMATS, true);
    if (parsed.isValid()) {
      return parsed.toDate();
    }
    const fallback = moment(trimmed);
    if (fallback.isValid()) {
      return fallback.toDate();
    }
  }
  return null;
};

const ensureDate = (event, candidates) => {
  for (const key of candidates) {
    if (event && Object.prototype.hasOwnProperty.call(event, key)) {
      const parsed = parseToDate(event[key]);
      if (parsed) {
        return parsed;
      }
    }
  }
  return null;
};

const normalizeEvent = (event) => {
  if (!event || typeof event !== "object") return null;
  const start = ensureDate(event, DATE_CANDIDATES) || parseToDate(event);
  if (!start) return null;

  let end =
    ensureDate(event, END_DATE_CANDIDATES) ||
    (event.durationMinutes
      ? moment(start).add(event.durationMinutes, "minutes").toDate()
      : null);

  if (!end || end < start) {
    end = moment(start).add(1, "hours").toDate();
  }

  const derivedTitle =
    event.title ||
    event.nome ||
    event.name ||
    event.descricao ||
    event.description ||
    moment(start).format("DD/MM/YYYY");

  return {
    ...event,
    title: derivedTitle,
    start,
    end,
    allDay: event.allDay ?? moment(start).hour() === 0,
  };
};

const Calendar = () => {
  const [events, setEvents] = useState([]); // Altere o nome da variável de 'data' para 'events'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(null);
  const [faturaSelecionada, setFaturaSelecionada] = useState(null);
  const [currentView, setCurrentView] = useState("month");
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const calendarMessages = useMemo(
    () => ({
      today: "Hoje",
      previous: "Anterior",
      next: "Próximo",
      month: "Mês",
      week: "Semana",
      day: "Dia",
      agenda: "Agenda",
      date: "Data",
      time: "Hora",
      event: "Evento",
      allDay: "Dia inteiro",
      noEventsInRange: "Sem faturas neste intervalo.",
      showMore: (total) => `+${total} itens`,
    }),
    []
  );
  const abortControllerRef = useRef(null);

  const auxDtInicial = useMemo(
    () => moment().subtract(90, "days").startOf("month"),
    []
  );
  const auxDtFinal = useMemo(
    () => moment().add(90, "days").endOf("month"),
    []
  );

  const dataInicial = useMemo(
    () => auxDtInicial.format("YYYY-MM-DD 00:00:00"),
    [auxDtInicial]
  );
  const dataFinal = useMemo(
    () => auxDtFinal.format("YYYY-MM-DD 23:59:59"),
    [auxDtFinal]
  );

  const loadEvents = useCallback(async () => {
    // Cancela qualquer request anterior antes de iniciar um novo ciclo
    if (abortControllerRef.current) {
      abortControllerRef.current.abort("replaced");
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // O axios do projeto (0.19) ignora `signal`: abortar não cancela o request.
    // Com o timeout via AbortController, uma resposta lenta (>10s, comum em
    // embarcador grande com o banco ocupado) chegava depois, era descartada por
    // estar "abortada" e o calendário ficava vazio sem erro. O timeout agora é
    // o do próprio axios; o AbortController só marca request substituído/desmontado.
    const REQUEST_TIMEOUT_MS = 60_000;

    const correlationId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `calendar-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const requestStartedAt = getTimestamp();

    setLoading(true);
    setError(null);

    try {
      const response = await api.get("/dashboard/auditoria/calendario", {
        params: {
          dataInicial,
          dataFinal,
        },
        signal: abortController.signal,
        timeout: REQUEST_TIMEOUT_MS,
        headers: {
          "X-Correlation-Id": correlationId,
        },
      });

      if (abortController.signal.aborted) {
        return;
      }

      if (response.status !== 200) {
        if (response.status === 204) {
          setEvents([]);
          setHasFetched(true);
          console.info(
            "[CalendarFatura] Requisição sem conteúdo (204)",
            {
              correlationId,
              durationMs: Math.round(getTimestamp() - requestStartedAt),
            }
          );
          return;
        }
        throw new Error(
          `Resposta inesperada da API (${response.status || "sem status"})`
        );
      }

      if (response.data == null) {
        setEvents([]);
        setHasFetched(true);
        console.info("[CalendarFatura] Requisição retornou payload vazio", {
          correlationId,
          durationMs: Math.round(getTimestamp() - requestStartedAt),
        });
        return;
      }

      if (!Array.isArray(response.data)) {
        throw new Error("Formato de resposta inválido.");
      }

      const normalizedEvents = response.data
        .map((event) => {
          const normalized = normalizeEvent(event);

          if (!normalized) {
            return null;
          }

          const composedTitle = buildEventTitle({
            ...(typeof event === "object" && event ? event : {}),
            ...normalized,
          });

          if (composedTitle) {
            normalized.title = composedTitle;
          }

          return normalized;
        })
        .filter(Boolean);

      const discardedEvents = response.data.length - normalizedEvents.length;
      if (discardedEvents > 0) {
        console.warn(
          "[CalendarFatura] Eventos descartados por dados inválidos",
          {
            correlationId,
            descartados: discardedEvents,
          }
        );
      }

      setEvents(normalizedEvents);
      setHasFetched(true);
      console.info(
        "[CalendarFatura] Requisição concluída com sucesso",
        {
          correlationId,
          durationMs: Math.round(getTimestamp() - requestStartedAt),
          quantidadeRegistros: normalizedEvents.length,
        }
      );
    } catch (err) {
      if (abortController.signal.aborted) {
        return;
      }

      if (err && err.code === "ECONNABORTED") {
        setError("Tempo limite excedido. Tente novamente.");
        setHasFetched(true);
        console.warn("[CalendarFatura] Requisição cancelada por timeout", {
          correlationId,
          durationMs: Math.round(getTimestamp() - requestStartedAt),
        });
        return;
      }

      console.error("[CalendarFatura] Falha ao carregar faturas", {
        correlationId,
        error: err,
      });
      setError("Não foi possível carregar as faturas. Tente novamente.");
      setHasFetched(true);
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
      console.info("[CalendarFatura] Ciclo da requisição encerrado", {
        correlationId,
        durationMs: Math.round(getTimestamp() - requestStartedAt),
        abortReason: abortController.signal.reason || null,
      });
    }
  }, [dataFinal, dataInicial]);

  useEffect(() => {
    loadEvents();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort("unmounted");
      }
    };
  }, [loadEvents]);

  const selectedEvent = (event) => {
    setFaturaSelecionada(event);
  };

  const handleShowMore = useCallback((events, date) => {
    setShowMoreInfo({
      events,
      date,
    });
  }, []);

  const handleViewChange = useCallback((view) => {
    if (view) {
      setCurrentView(view);
    }
    setShowMoreInfo(null);
  }, []);

  const handleNavigate = useCallback((date) => {
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      setCurrentDate(date);
    }
    setShowMoreInfo(null);
  }, []);

  const eventColors = (event, start, end, isSelected) => {
    var backgroundColor = "event-";
    event.color
      ? (backgroundColor = backgroundColor + event.color)
      : (backgroundColor = backgroundColor + "default");
    // console.log(backgroundColor);
    return {
      className: backgroundColor,
    };
  };
  // Restante do código do componente...

  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center" }}>
          <ClipLoader
            sizeUnit="px"
            size={90}
            color="#1d8cf8"
            loading={loading}
          />
          <br />
          <br />
          <h1 className="text-info" style={{ textAlign: "center" }}>
            Carregando ...
          </h1>
        </div>
      );
    }

    if (error) {
      return (
        <div style={{ textAlign: "center" }}>
          <h4 className="text-danger" style={{ marginBottom: "16px" }}>
            {error}
          </h4>
          <Button color="info" onClick={loadEvents}>
            Tentar novamente
          </Button>
        </div>
      );
    }

    if (hasFetched && events.length === 0) {
      return (
        <div style={{ textAlign: "center" }}>
          <h4 className="text-muted" style={{ marginBottom: "8px" }}>
            Sem faturas no período selecionado.
          </h4>
          <p style={{ marginBottom: 0 }}>
            Ajuste os filtros ou verifique novamente mais tarde.
          </p>
        </div>
      );
    }

    return (
      <BigCalendar
        localizer={localizer}
        events={events} // Utilize o estado 'events' no componente BigCalendar
        view={currentView}
        date={currentDate}
        popup
        defaultView="month"
        scrollToTime={new Date(1970, 1, 1, 6)}
        defaultDate={new Date()}
        onSelectEvent={(event) => selectedEvent(event)}
        onShowMore={handleShowMore}
        onDrillDown={(date, view) => {
          setShowMoreInfo(null);
          if (view) {
            setCurrentView(view);
          }
          if (date instanceof Date && !Number.isNaN(date.getTime())) {
            setCurrentDate(date);
          }
        }}
        onView={handleViewChange}
        onNavigate={handleNavigate}
        messages={calendarMessages}
        culture="pt-br"
        eventPropGetter={eventColors}
      />
    );
  };

  return (
    <>
      <div className="content">
        <Row>
          <Col className="ml-auto mr-auto col-md-12 calendar-col" md="12">
            <Card className="card card-calendar">
              <CardBody>
                <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
                  Faturas por Vencimento
                </h3>{" "}
                {/* Cabeçalho centralizado */}
                {renderContent()}
              </CardBody>
            </Card>
          </Col>
        </Row>
        <CalendarPopupModal
          info={showMoreInfo}
          onClose={() => setShowMoreInfo(null)}
          onSelectEvent={selectedEvent}
          localizer={localizer}
        />
        <FaturaDetalheModal
          fatura={faturaDoEvento(faturaSelecionada)}
          onClose={() => setFaturaSelecionada(null)}
        />
      </div>
    </>
  );
};

const CalendarPopupModal = ({ info, onClose, onSelectEvent, localizer }) => {
  useEffect(() => {
    if (!info) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [info, onClose]);

  if (!info) {
    return null;
  }

  const title = moment(info.date).format("dddd, DD [de] MMMM");

  if (typeof document === "undefined") {
    return null;
  }

  const modalContent = (
    <div className="calendar-popup-backdrop" onClick={onClose}>
      <div
        className="calendar-popup-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="calendar-popup-header">
          <h5>
            {title.charAt(0).toUpperCase() + title.slice(1)}
          </h5>
          <button
            type="button"
            className="calendar-popup-close"
            aria-label="Fechar"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="calendar-popup-body">
          <div className="calendar-popup-list">
            {info.events && info.events.length > 0 ? (
              info.events.map((event, index) => (
                <button
                  key={`${event.id || event.title}-${index}`}
                  type="button"
                  className="calendar-popup-item"
                  onClick={() => {
                    onSelectEvent(event);
                    onClose();
                  }}
                >
                  <span>{event.title}</span>
                  {event.status && (
                    <span className="calendar-popup-item__tag">
                      {event.status}
                    </span>
                  )}
                </button>
              ))
            ) : (
              <span className="text-muted">
                Nenhuma fatura disponível para esta data.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

// Mesma régua de cor que o backend usa para o evento (DashboardServices.getFaturaCalendar).
// Verde (paga) fica de fora: a linha "Situação" já diz isso.
const PRAZO_POR_COR = {
  purple: { label: "Vencida", cor: "#ff5c7a" },
  red: { label: "Vence em até 7 dias", cor: "#ff9f43" },
  yellow: { label: "Vence entre 7 e 15 dias", cor: "#f7d154" },
  blue: { label: "Vence em mais de 15 dias", cor: "#4fa3ff" },
};

// Evento do calendário -> props do modal compartilhado. Sem numFatura é o
// backend antigo, que só manda o title.
const faturaDoEvento = (event) =>
  event && {
    numFatura: event.numFatura,
    valor: event.valor,
    transportadora: event.transportadora,
    situacao: rotuloSituacao(event.situacao, event.situacaoNome),
    vencimento: event.vencimento,
    emissao: event.emissao,
    qtdCtes: event.qtdCtes,
    idFatura: event.idFatura,
    prazo: PRAZO_POR_COR[event.color],
    titulo: event.title,
  };

export default Calendar;
